import { z } from 'zod';

import { apiClient } from '@/lib/api-client';

/**
 * `POST /api/v1/conversations/{id}/read-receipt` — WhatsApp read receipts
 * (blue ticks) for what an agent has verifiably SEEN. 2026-09 F4: Contract C
 * (`docs/contracts/hub-frontend-api.md`), spec
 * `docs/specs/2026-09-features/04-two-way-read-sync.md` §4.1; hub route
 * `hub/app/api/read_receipts.py`, service `hub/app/read_sync.py`.
 *
 * The ONLY caller is `useReadReceiptTrigger` — the strict on-view trigger
 * (tab visible AND focused, list at the newest message, input within 3
 * minutes, all held for 2 s). It is deliberately separate from
 * `POST .../read` (`markConversationRead`), which is console bookkeeping,
 * fires for hidden tabs too, and never sends a WhatsApp receipt.
 *
 * The hub owns every other gate (mode, the `ICGCHAT_READ_RECEIPTS_ACCOUNTS`
 * pilot allow-list, account connected, direct chat, wamid, max age, the claim
 * watermark) and answers a refusal as a `200` with a `skipped` reason — never
 * a `409`. `404` unknown conversation; `422` malformed body.
 */

/** `mode` — mirrors the hub's `ReadReceiptsMode = Literal["off", "on_view"]`
 *  (`hub/app/config/settings.py`), echoing `ICGCHAT_READ_RECEIPTS_MODE`
 *  (default `off`). A console that sees `off` backs off for minutes. */
export const READ_RECEIPTS_MODES = ['off', 'on_view'] as const;

/** `skipped` — mirrors the hub's `ReadReceiptSkip` Literal
 *  (`hub/app/read_sync.py`): why THIS call claimed nothing. `null` when it
 *  claimed at least one message. `disabled` pairs with `mode: "off"`. */
export const READ_RECEIPT_SKIP_REASONS = [
  'disabled',
  'account_not_allowed',
  'not_direct',
  'account_not_connected',
  'nothing_new',
  'gateway_unavailable',
] as const;

/** Mirror of the hub's `ReadReceiptOut` (`hub/app/api/schemas.py`). Strict
 *  enums on purpose: the literals above are the whole contract, and a value
 *  outside them is a contract drift that must surface as a parse error (the
 *  trigger treats it like any failed call — logged, never retried). */
export const readReceiptOutSchema = z.object({
  mode: z.enum(READ_RECEIPTS_MODES),
  /** Inbound messages THIS call claimed for a receipt; the WhatsApp RPC
   *  itself runs in the background on the hub. */
  receipted: z.number().int().nonnegative(),
  skipped: z.enum(READ_RECEIPT_SKIP_REASONS).nullable(),
});

export type ReadReceiptResult = z.infer<typeof readReceiptOutSchema>;
export type ReadReceiptsMode = ReadReceiptResult['mode'];
export type ReadReceiptSkip = NonNullable<ReadReceiptResult['skipped']>;

/**
 * Ask the hub to send WhatsApp read receipts for one conversation, up to and
 * including `seenThrough`.
 *
 * `seenThrough` MUST be the server's own `created_at` string of the newest
 * message the agent saw, passed through VERBATIM: the hub stores microseconds
 * and claims `created_at <= seen_through`, so a millisecond re-serialization
 * (`new Date(...).toISOString()`) would fall short of the very row it names.
 * It is timezone-aware as served (a naive timestamp is a `422`).
 */
export async function requestReadReceipt(
  conversationId: string,
  seenThrough: string,
): Promise<ReadReceiptResult> {
  const response = await apiClient.post(`/conversations/${conversationId}/read-receipt`, {
    seen_through: seenThrough,
  });
  return readReceiptOutSchema.parse(response.data);
}
